import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { X, ShoppingBag, ChevronUp } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useStoreTypeRedirect } from "@/components/layout/store-type-redirect";
import { ShowroomPartnerPicker } from "@/components/wholesale/showroom-partner-picker";
import { ShowroomGrid } from "@/components/wholesale/showroom-grid";
import { ShowroomItemSheet } from "@/components/wholesale/showroom-item-sheet";
import { ShowroomCartSheet } from "@/components/wholesale/showroom-cart-sheet";
import { shippingFromPartner } from "@/components/wholesale/constants";
import { usePartner, useWholesaleContext, useCreateWholesaleOrder, type PartnerListItem } from "@/hooks/use-wholesale";
import { useShowroomCart } from "@/hooks/use-showroom-cart";
import { editorItemKey, editorTotals, type EditorItem } from "@/lib/wholesale-items";
import { fmtKm, fmtPct } from "@/lib/wholesale-format";
import type { CatalogRow } from "@/services/wholesale-catalog";

/** Prezentacijski katalog: ekran koji se okreće prema kupcu. Bez sidebara i header trake —
 *  slika artikla i cijena su jedino što se s druge strane stola vidi. */
export function WholesaleShowroomPage() {
  const navigate = useNavigate();
  // Ekran nema sidebar, pa nema ni njegovu straža tipa trgovine: bez ovoga bi se
  // prezentacija otvarala nad maloprodajnom trgovinom, a svaka veleprodajna stranica
  // do koje vodi (nacrt narudžbe) bi te odmah vratila na maloprodaju.
  useStoreTypeRedirect();
  const cart = useShowroomCart();
  const partner = usePartner(cart.cart.partnerId ?? undefined);
  const ctx = useWholesaleContext();
  const createOrder = useCreateWholesaleOrder();

  const [open, setOpen] = useState<CatalogRow | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);

  const vatRate = ctx.data?.vatRate ?? 0;
  const partnerDiscount = partner.data?.discountPct ?? 0;
  const storeDiscounts = partner.data?.storeDiscounts ?? [];
  const totals = editorTotals(cart.cart.items, vatRate);

  function pickPartner(p: PartnerListItem) {
    cart.setPartnerId(p.id);
    // Lista partnera već nosi rabate, pa se cijene u korpi poravnaju odmah — bez čekanja
    // na detalj partnera koji tek kreće po mreži.
    cart.reprice(p.discountPct, p.storeDiscounts);
  }

  function submitItem(line: EditorItem, replace: boolean) {
    if (replace) cart.setQuantity(editorItemKey(line), line.quantity);
    else cart.add(line);
  }

  /** Brzo dodavanje s kartice: stepper zadaje konačnu količinu, a ne prirast — postojeća
   *  linija se prepiše, nova se doda, nula izbaci artikal iz korpe. */
  function setLineQuantity(line: EditorItem, quantity: number) {
    const key = editorItemKey(line);
    if (quantity < 1) {
      cart.remove(key);
      return;
    }
    if (cart.quantityOf(key) > 0) cart.setQuantity(key, quantity);
    else cart.add({ ...line, quantity });
  }

  function leave() {
    navigate({ to: "/wholesale/catalog" });
  }

  async function createDraft() {
    const p = partner.data;
    if (!p) {
      toast.error("Partner nije učitan, pokušaj ponovo");
      return;
    }
    try {
      const order = await createOrder.mutateAsync({
        partnerId: p.id,
        shipping: shippingFromPartner(p),
        items: cart.cart.items.map((i) => ({
          storeId: i.storeId, wcProductId: i.wcProductId, wcVariationId: i.wcVariationId,
          quantity: i.quantity, overridePrice: null,
        })),
      });
      cart.clear();
      toast.success("Nacrt narudžbe sačuvan");
      navigate({ to: "/wholesale/orders/$orderId", params: { orderId: order.id } });
    } catch (e: any) {
      toast.error(e?.message || "Narudžba nije sačuvana");
    }
  }

  if (!cart.cart.partnerId) {
    return (
      <div className="min-h-screen bg-background">
        <button
          type="button"
          onClick={leave}
          aria-label="Zatvori prezentaciju"
          className="fixed left-2 top-2 z-30 flex h-11 w-11 items-center justify-center text-muted-foreground"
        >
          <X className="h-5 w-5" />
        </button>
        <ShowroomPartnerPicker onPick={pickPartner} />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-background pb-16">
      {/* Ikona zatvaranja i tekst partnera sjedaju na iste lijeve ivice kao lupa i polje
          pretrage u traci ispod — dva sticky reda čitaju se kao jedna cjelina. */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-1.5 border-b bg-background px-3">
        <button
          type="button"
          onClick={() => (cart.count > 0 ? setExitOpen(true) : leave())}
          aria-label="Zatvori prezentaciju"
          className="-ml-2.5 flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground transition-colors active:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => cart.setPartnerId(null)}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
        >
          <span className="w-full truncate text-sm font-medium">{partner.data?.name ?? "Učitavanje…"}</span>
          <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {partnerDiscount > 0 ? `Rabat ${fmtPct(partnerDiscount)} · promijeni partnera` : "Promijeni partnera"}
          </span>
        </button>
      </header>

      <ShowroomGrid
        partnerDiscount={partnerDiscount}
        storeDiscounts={storeDiscounts}
        quantityOf={cart.quantityOfProduct}
        quantityOfLine={cart.quantityOf}
        onSetQuantity={setLineQuantity}
        onOpen={setOpen}
      />

      {cart.count > 0 && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center gap-3 bg-foreground px-4 text-background"
        >
          <ShoppingBag className="h-5 w-5 shrink-0" />
          <span className="text-sm tabular-nums">
            {cart.count} {cart.count === 1 ? "komad" : "komada"}
            <span className="opacity-60"> · {cart.cart.items.length} art.</span>
          </span>
          <span className="ml-auto text-lg font-semibold tabular-nums">{fmtKm(totals.total)}</span>
          <ChevronUp className="h-4 w-4 shrink-0 opacity-70" />
        </button>
      )}

      <ShowroomItemSheet
        item={open}
        partnerDiscount={partnerDiscount}
        storeDiscounts={storeDiscounts}
        quantityInCart={cart.quantityOf}
        onSubmit={submitItem}
        onClose={() => setOpen(null)}
      />

      <ShowroomCartSheet
        open={cartOpen}
        onOpenChange={setCartOpen}
        items={cart.cart.items}
        vatRate={vatRate}
        partnerName={partner.data?.name ?? ""}
        saving={createOrder.isPending}
        onSetQuantity={cart.setQuantity}
        onRemove={cart.remove}
        onCreateOrder={createDraft}
      />

      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Napustiti prezentaciju?</AlertDialogTitle>
            <AlertDialogDescription>
              Korpa ima {cart.count} {cart.count === 1 ? "komad" : "komada"} u vrijednosti {fmtKm(totals.total)}.
              Ostaje sačuvana i kad se vratiš.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ostani</AlertDialogCancel>
            <AlertDialogAction onClick={leave}>Napusti</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
